import { useMemo } from 'react'
import { Space, Tag, TreeSelect, Typography } from 'antd'
import type { TreeSelectProps } from 'antd'
import { useTranslation } from 'react-i18next'
import {
  buildOrgTree,
  kindName,
  type OrgNode,
  type OrgNodeType,
  type OrgTreeNode,
} from '@/features/org/orgApi'

type TreeNode = NonNullable<TreeSelectProps['treeData']>[number]

interface OrgUnitTreeSelectProps {
  units: readonly OrgNode[]
  types: readonly OrgNodeType[]
  value?: string | null
  onChange?: (value: string | null) => void
  /** Already linked unit: marked in the tree and kept selectable even when closed. */
  currentId?: string | null
  allowClear?: boolean
  disabled?: boolean
  loading?: boolean
}

function useOrgTreeData(units: readonly OrgNode[], types: readonly OrgNodeType[], currentId: string | null) {
  const { t } = useTranslation()

  const treeData = useMemo<TreeNode[]>(() => {
    const convert = (unit: OrgTreeNode): TreeNode | null => {
      const children = unit.children
        .map(convert)
        .filter((child): child is TreeNode => child !== null)
      const selectable = unit.state !== 'CLOSED' || unit.id === currentId
      if (!selectable && children.length === 0) return null

      return {
        value: unit.id,
        selectable,
        title: (
          <Space size={6} wrap>
            <Typography.Text type={selectable ? undefined : 'secondary'}>
              {unit.displayName}
            </Typography.Text>
            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
              {kindName(types, unit.kind, t)}
            </Typography.Text>
            {unit.id === currentId ? <Tag color="blue">{t('identity.partyNode.currentTag')}</Tag> : null}
            {selectable ? null : (
              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                {t('identity.partyNode.closedNote')}
              </Typography.Text>
            )}
          </Space>
        ),
        children,
      }
    }

    return buildOrgTree(units)
      .map(convert)
      .filter((node): node is TreeNode => node !== null)
  }, [units, types, currentId, t])

  const searchText = useMemo(
    () => new Map(units.map((unit) => [unit.id, `${unit.displayName} ${unit.code}`.toLowerCase()])),
    [units],
  )

  const filterTreeNode: TreeSelectProps['filterTreeNode'] = (input, node) => {
    const key = node?.value === undefined ? '' : String(node.value)
    return (searchText.get(key) ?? '').includes(input.toLowerCase())
  }

  return { treeData, filterTreeNode }
}

/** Any open unit of the organisational structure, searchable by name or code. */
export function OrgUnitTreeSelect({
  units,
  types,
  value,
  onChange,
  currentId = null,
  allowClear = false,
  disabled = false,
  loading = false,
}: OrgUnitTreeSelectProps) {
  const { t } = useTranslation()
  const { treeData, filterTreeNode } = useOrgTreeData(units, types, currentId)

  return (
    <TreeSelect
      value={value ?? undefined}
      onChange={(next: string | undefined) => onChange?.(next ?? null)}
      treeData={treeData}
      showSearch
      treeDefaultExpandAll
      allowClear={allowClear}
      disabled={disabled}
      loading={loading}
      filterTreeNode={filterTreeNode}
      placeholder={t('identity.partyNode.placeholder')}
      notFoundContent={t('identity.partyNode.emptyTree')}
      style={{ width: '100%' }}
    />
  )
}

/** Several units at once — for example a role granted on several branches. */
export function OrgUnitTreeMultiSelect({
  units,
  types,
  value,
  onChange,
  disabled = false,
  loading = false,
}: {
  units: readonly OrgNode[]
  types: readonly OrgNodeType[]
  value?: string[]
  onChange?: (value: string[]) => void
  disabled?: boolean
  loading?: boolean
}) {
  const { t } = useTranslation()
  const { treeData, filterTreeNode } = useOrgTreeData(units, types, null)
  return (
    <TreeSelect
      multiple
      value={value ?? []}
      onChange={(next: string[]) => onChange?.(next)}
      treeData={treeData}
      showSearch
      treeDefaultExpandAll
      allowClear
      disabled={disabled}
      loading={loading}
      filterTreeNode={filterTreeNode}
      placeholder={t('identity.partyNode.placeholder')}
      notFoundContent={t('identity.partyNode.emptyTree')}
      style={{ width: '100%' }}
    />
  )
}
